import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Container, Alert, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { BASE_URL } from '../../config';
import { readStorage, writeStorage } from '../portal/portalAuth';
import StickyActionBar, { fullScreenHeight } from '../../components/common/StickyActionBar';

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;
// sessionStorage: "<phone>:<sessionid>" from generate-otp, so a refresh doesn't send another SMS
const OTP_SESSION_KEY = 'verify_otp_session';

// Shown right after the register form is submitted; on success leads to the "portal access is ready" screen.
export default function OtpVerificationPage() {
  const [phone] = useState(() => readStorage('local', 'phone'));
  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const [sessionId, setSessionId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const sentOnMount = useRef(false);

  const sendOtp = async (isResend: boolean) => {
    if (!phone) return;
    setIsSending(true);
    setErrorMessage('');
    setInfoMessage('');
    try {
      const response = await fetch(`${BASE_URL}/api/form/generate-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ phone_number: phone }),
      });
      const data = await response.json().catch(() => ({}));
      const newSessionId = data.sessionid || data.session_id;
      if (!response.ok || data.success === false || !newSessionId) {
        throw new Error(data.error || data.message || 'Could not send OTP. Please try again.');
      }
      setSessionId(newSessionId);
      writeStorage('session', OTP_SESSION_KEY, `${phone}:${newSessionId}`);
      setResendIn(RESEND_SECONDS);
      if (isResend) setInfoMessage(`OTP sent again to +91 ${phone}`);
    } catch (error: any) {
      setErrorMessage(error.message || 'Could not send OTP. Please try again.');
      setResendIn(0);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    // Ref guard: StrictMode runs effects twice in dev, which would send two SMS
    if (!phone || sentOnMount.current) return;
    sentOnMount.current = true;
    const saved = readStorage('session', OTP_SESSION_KEY);
    if (saved && saved.startsWith(`${phone}:`)) {
      setSessionId(saved.slice(phone.length + 1));
      return;
    }
    sendOtp(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Reached directly without registering
  if (!phone) return <Navigate to="/register" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInfoMessage('');

    if (otp.length !== OTP_LENGTH) {
      setErrorMessage(`Please enter the ${OTP_LENGTH}-digit OTP`);
      return;
    }
    if (!sessionId) {
      setErrorMessage('OTP was not sent. Please tap Resend OTP.');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(`${BASE_URL}/api/form/verify-token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phone, otp, session_id: sessionId, sessionid: sessionId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        setErrorMessage(data.error || data.message || 'Incorrect OTP. Please try again.');
        return;
      }
    } catch {
      setErrorMessage('Network error. Please check your connection and try again.');
      return;
    } finally {
      setIsVerifying(false);
    }

    try { sessionStorage.removeItem(OTP_SESSION_KEY); } catch {}

    // replace(): back button shouldn't return to the OTP step
    const adSource = sessionStorage.getItem('ad_source');
    if (adSource && (adSource === 'facebook' || adSource === 'meta' || adSource === 'fb')) {
      window.location.replace('/payment-processing-fb');
    } else {
      window.location.replace('/payment-processing');
    }
  };

  const handleResend = () => {
    if (isSending) return;
    setOtp('');
    sendOtp(true);
  };

  return (
    <Box sx={{ ...fullScreenHeight, bgcolor: '#F2F5F1', p: 2, pb: 0, display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 4 }}>

        {/* Headings */}
        <Typography sx={{ color: '#0F231A', fontSize: '32px', fontWeight: 700, lineHeight: 1.1, mb: 1, fontFamily: '"Moan Slight", sans-serif', letterSpacing: '-1px' }}>
          Verify your number
        </Typography>
        <Typography sx={{ color: '#666', fontSize: '15px', mb: 4, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          {isSending && !sessionId ? `Sending OTP to +91 ${phone}...` : `Enter the OTP sent to +91 ${phone}`}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flexGrow: 1 }}>

          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ borderRadius: '12px' }}>
              {errorMessage}
            </Alert>
          )}
          {infoMessage && (
            <Alert severity="success" onClose={() => setInfoMessage('')} sx={{ borderRadius: '12px' }}>
              {infoMessage}
            </Alert>
          )}

          {/* OTP */}
          <Box>
            <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 700, mb: 1, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              One-time password
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder={'•'.repeat(OTP_LENGTH)}
                maxLength={OTP_LENGTH}
                value={otp}
                onChange={(e) => {
                  setErrorMessage('');
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH));
                }}
                style={{ width: '100%', border: 'none', padding: '14px 16px', fontSize: '22px', letterSpacing: '10px', textAlign: 'center', outline: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              />
            </Box>
            <Typography sx={{ color: '#666', fontSize: '13px', mt: 1.5, textAlign: 'center', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              {isSending ? (
                'Sending OTP...'
              ) : resendIn > 0 ? (
                `Resend OTP in ${resendIn}s`
              ) : (
                <Box component="span" onClick={handleResend} sx={{ color: '#0F763F', fontWeight: 700, cursor: 'pointer' }}>
                  Resend OTP
                </Box>
              )}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <StickyActionBar>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={otp.length !== OTP_LENGTH || isVerifying || !sessionId}
              sx={{
                bgcolor: '#0F763F !important',
                color: '#fff',
                py: 2,
                borderRadius: '16px',
                fontSize: '17px',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                '&:hover': { bgcolor: '#0d6535 !important', boxShadow: 'none' },
                '&:disabled': { bgcolor: '#A5D6B6 !important', color: '#fff' }
              }}
            >
              {isVerifying ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                  Verifying...
                </>
              ) : (
                'Verify & continue'
              )}
            </Button>
          </StickyActionBar>
        </Box>
      </Container>
    </Box>
  );
}
